import { useTranslation } from 'react-i18next';

import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { type SidebarNavBadgeLogic, type SidebarNavBadgeProps } from './SidebarNavBadge.types';

/** Past this the number stops informing and starts shouting; the list says the rest. */
const CAP = 99;

/**
 * The count a nav row wears (F3).
 *
 * The hook the item carries is called HERE, unconditionally — the row renders
 * this component only when the item declares one, so the rules of hooks hold
 * without an `item.useBadge?.()` in the row. `undefined` (not asked yet, or
 * not allowed to ask) and `0` both draw nothing: a badge saying zero is a row
 * shouting that there is nothing to do.
 */
export function useSidebarNavBadgeLogic({
  useCount,
  label,
}: SidebarNavBadgeProps): SidebarNavBadgeLogic {
  const { t, i18n } = useTranslation('uiKit');
  const count = useCount();
  const locale = i18n.language.startsWith('ar') ? 'ar' : 'en';

  if (count === undefined || count <= 0) {
    return { isShown: false, text: '', accessibleLabel: '' };
  }

  const capped = Math.min(count, CAP);

  return {
    isShown: true,
    text:
      count > CAP
        ? t('shell.badgeOverflow', { count: formatNumber(CAP, locale) })
        : formatNumber(capped, locale),
    accessibleLabel: t('shell.badgeLabel', { count, label }),
  };
}
